import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiCode, FiDatabase, FiLayers, FiGlobe, FiSmartphone } from 'react-icons/fi';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(74, 222, 128, 0.2); }
  50% { box-shadow: 0 0 20px rgba(74, 222, 128, 0.4); }
  100% { box-shadow: 0 0 5px rgba(74, 222, 128, 0.2); }
`;

const BannerContainer = styled.section`
  padding: 4rem 1rem;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 8rem 2rem;
  }
`;

const DeviceMockupsWrapper = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  
  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 4rem;
  }
`;

const BrowserMockup = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 100%;
`;

const BrowserHeader = styled.div`
  background: #f1f5f9;
  padding: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
`;

const BrowserButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-right: 1rem;

  span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    
    &:nth-child(1) { background: #ff5f57; }
    &:nth-child(2) { background: #ffbd2e; }
    &:nth-child(3) { background: #28c940; }
  }
`;

const AddressBar = styled.div`
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-family: monospace;
  color: #64748b;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #94a3b8;
  }
`;

const BrowserContent = styled.div`
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 1200px) {
    grid-template-columns: 0.8fr 1.2fr;
    gap: 4rem;
    padding: 3rem;
  }
`;

const ContentLeft = styled.div`
  h1 {
    font-size: 2rem;
    text-align: center;
    
    @media (min-width: 768px) {
      font-size: 3.5rem;
      text-align: left;
    }
  }

  p {
    font-size: 1rem;
    text-align: center;
    
    @media (min-width: 768px) {
      font-size: 1.2rem;
      text-align: left;
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const FeatureItem = styled(motion.div)`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-25px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 1.5rem;
    color: #0ea5e9;
  }

  h3 {
    font-size: 1rem;
    color: #334155;
    margin: 0;
  }
`;

const ContentRight = () => (
  <CodeBlock>
    <pre>
      <code>
        <span className="keyword">const</span> createApp = <span className="keyword">async</span> () => {'{'}
        <br />  <span className="comment">// Initialize modern stack</span>
        <br />  <span className="keyword">const</span> app = <span className="keyword">new</span> WebApp({'{'}
        <br />    frontend: <span className="string">'React'</span>,
        <br />    backend: <span className="string">'Node.js'</span>,
        <br />    database: <span className="string">'PostgreSQL'</span>
        <br />  {'}'});
        <br />
        <br />  <span className="comment">// Add enterprise features</span>
        <br />  await app.configure({'{'}
        <br />    security: <span className="string">'enterprise'</span>,
        <br />    scaling: <span className="string">'auto'</span>,
        <br />    performance: <span className="string">'optimized'</span>
        <br />  {'}'});
        <br />
        <br />  <span className="keyword">return</span> app;
        <br />{'}'}
      </code>
    </pre>
  </CodeBlock>
);

const CodeBlock = styled.div`
  padding: 1.5rem;
  font-size: 0.8rem;
  overflow-x: auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
    font-size: 0.9rem;
  }
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  animation: ${float} 6s ease-in-out infinite;
  filter: blur(8px);
  opacity: 0.5;
  z-index: -1;
`;

const PhoneMockup = styled.div`
  width: 280px;
  height: 560px;
  position: absolute;
  border-radius: 30px;
  background: white;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.5s ease;
  
  // Phone frame design
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 24px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    border-radius: 30px 30px 0 0;
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: #334155;
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    width: 300px;
    height: 600px;
    
    &[position="front"] {
      transform: rotateY(-15deg) translateX(-30px);
      z-index: 2;
    }
    
    &[position="back"] {
      transform: rotateY(15deg) translateX(30px);
      z-index: 1;
    }
  }
  
  @media (max-width: 767px) {
    position: absolute;
    width: 260px;
    height: 520px;
    transition: all 0.3s ease;
    
    &[position="front"] {
      transform: translateY(-30px) translateX(-20px);
      z-index: 2;
    }
    
    &[position="back"] {
      transform: translateY(30px) translateX(20px);
      z-index: 1;
      opacity: 0.9;
    }
  }

  @media (max-width: 480px) {
    width: 240px;
    height: 480px;
    
    &[position="front"] {
      transform: translateY(-20px) translateX(-15px);
    }
    
    &[position="back"] {
      transform: translateY(20px) translateX(15px);
    }
  }
`;

const PhonesContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  perspective: 1000px;
  height: auto;
  width: 100%;
  max-width: 400px;
  padding: 1rem;
  
  @media (min-width: 1200px) {
    height: 700px;
    width: 400px;
    padding: 0;
  }

  @media (max-width: 767px) {
    height: 600px;
    perspective: none;
  }

  &:hover {
    @media (min-width: 768px) {
      ${PhoneMockup}[position="front"] {
        transform: rotateY(-15deg) translateX(-80px);
      }
      
      ${PhoneMockup}[position="back"] {
        transform: rotateY(15deg) translateX(80px);
      }
    }
  }
`;

const PhoneScreen = styled.div`
  height: calc(100% - 24px); // Adjust for the frame header
  margin-top: 24px; // Push content below the frame header
  background: #1e293b;
  padding: 1.5rem;
  font-family: 'Fira Code', monospace;
  color: #e2e8f0;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #334155;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 4px;
  }
  
  pre {
    margin: 0 0 1rem 0;
    code {
      color: #94a3b8;
      font-size: 0.7rem;
      line-height: 1.4;
      white-space: pre-wrap;
      .keyword { color: #93c5fd; }
      .string { color: #86efac; }
      .comment { color: #64748b; }
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const iOSCode = () => (
  <pre>
    <code>
      <span className="keyword">class</span> MorpheusApp: <span className="keyword">UIViewController</span> {'{'}
      <br />  <span className="comment">// iOS Native Setup</span>
      <br />  <span className="keyword">func</span> configure() {'{'}
      <br />    <span className="keyword">let</span> app = SwiftApp({'{'}
      <br />      ui: <span className="string">"SwiftUI"</span>,
      <br />      storage: <span className="string">"CoreData"</span>,
      <br />      security: <span className="string">"Keychain"</span>
      <br />    {'}'})
      <br />  {'}'}
      <br />{'}'}
    </code>
  </pre>
);

const androidCode = () => (
    <>
    <pre>
    <code>
      <span className="comment">// Morpheus Tech's Expertise in App Development</span>
      <br /><span className="keyword">val</span> morpheusAdvantages = {'{'}
      <br />  ai = <span className="string">"Seamless ML Kit integration"</span>,
      <br />  cloud = <span className="string">"Expertise in Firebase, AWS, and GCP"</span>,
      <br />  testing = <span className="string">"Comprehensive automated and manual testing"</span>,
      <br />  analytics = <span className="string">"Real-time performance metrics"</span>,
      <br />  cicd = <span className="string">"Fully automated CI/CD pipelines"</span>,
      <br />  support = <span className="string">"24/7 dedicated maintenance"</span>
      <br />{'}'}
    </code>
  </pre>
  <pre>
    <code>
      <span className="comment">// Tailored Development Solutions</span>
      <br /><span className="keyword">val</span> tailoredSolutions = {'{'}
      <br />  scalability = <span className="string">"Apps designed to grow with your business"</span>,
      <br />  performance = <span className="string">"Optimized for speed and reliability"</span>,
      <br />  crossPlatform = <span className="string">"Seamless support for Android, iOS, and web"</span>,
      <br />  security = <span className="string">"Advanced encryption and biometric integration"</span>
      <br />{'}'}
    </code>
  </pre>
  <pre>
    <code>
      <span className="comment">// End-to-End Development Experience</span>
      <br /><span className="keyword">val</span> endToEnd = {'{'}
      <br />  consultation = <span className="string">"Detailed planning and requirement analysis"</span>,
      <br />  design = <span className="string">"User-centric UI/UX tailored to your audience"</span>,
      <br />  development = <span className="string">"Efficient coding with cutting-edge technologies"</span>,
      <br />  delivery = <span className="string">"Timely project completion with guaranteed quality"</span>
      <br />{'}'}
    </code>
  </pre>
  </>
);

const Web2Banner = () => {
  return (
    <BannerContainer>
      <DeviceMockupsWrapper>
        <BrowserMockup>
          <BrowserHeader>
            <BrowserButtons>
              <span></span>
              <span></span>
              <span></span>
            </BrowserButtons>
            <AddressBar>
              <FiGlobe />
              https://morpheus-tech.io/web2-services
            </AddressBar>
          </BrowserHeader>
          <BrowserContent>
            <ContentLeft>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Web2 Development Services
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Transform your digital presence with our cutting-edge web development solutions. 
                We build scalable, secure, and high-performance applications tailored to your needs.
              </motion.p>
              <FeatureGrid>
                {[
                  { icon: <FiCode />, title: "Modern Frontend" },
                  { icon: <FiDatabase />, title: "Robust Backend" },
                  { icon: <FiLayers />, title: "Scalable Architecture" },
                  { icon: <FiGlobe />, title: "Global Deployment" }
                ].map((feature, index) => (
                  <FeatureItem
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  >
                    {feature.icon}
                    <h3>{feature.title}</h3>
                  </FeatureItem>
                ))}
              </FeatureGrid>
            </ContentLeft>
            <ContentRight />
          </BrowserContent>
        </BrowserMockup>
        <PhonesContainer>
          
          <PhoneMockup position="front">
            <PhoneScreen>
              {androidCode()}
            </PhoneScreen>
          </PhoneMockup>
          <PhoneMockup position="back">
            <PhoneScreen>
              {iOSCode()}
            </PhoneScreen>
          </PhoneMockup>
        </PhonesContainer>
      </DeviceMockupsWrapper>
    </BannerContainer>
  );
};

export default Web2Banner; 